import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { ApiError } from '../types';

// Event to notify about auth errors
export const AUTH_ERROR_EVENT = 'api:auth-error';

// Auth service using Amplify v6
const AuthService = {
  // In Amplify v6, we use fetchAuthSession directly
  fetchAuthSession: async (options?: { forceRefresh?: boolean }) => {
    return await fetchAuthSession(options);
  }
};

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store a reference to current refresh promise for deduplication
let refreshPromise: Promise<boolean> | null = null;

/**
 * Refresh auth tokens and return the new token
 */
const refreshAuthToken = async (): Promise<string | null> => {
  // If a refresh is already in progress, reuse that promise
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        // Use fetchAuthSession with forceRefresh for Amplify v6
        const session = await AuthService.fetchAuthSession({ forceRefresh: true });
        return !!session.tokens?.idToken;
      } catch (error) {
        console.error('Failed to refresh token:', error);
        
        // Dispatch auth error event
        window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { 
          detail: { message: 'Session expired' } 
        }));
        
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }

  // Wait for the refresh to complete
  const success = await refreshPromise;
  
  // Get the new token if refresh was successful
  if (success) {
    try {
      const session = await AuthService.fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error getting token after refresh:', error);
      return null;
    }
  }
  
  return null;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Get current session using Amplify v6 fetchAuthSession
      const session = await AuthService.fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      // Add token to headers
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    } catch (error) {
      // Try to refresh the token if getting the session failed
      const refreshedToken = await refreshAuthToken();
      
      // Add refreshed token to headers if available
      if (refreshedToken && config.headers) {
        config.headers.Authorization = `Bearer ${refreshedToken}`;
      }
      
      // If no auth is available, proceed without token
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    // Format error for consistent handling
    const apiError: ApiError = {
      code: error.code || 'ERROR',
      message: error.message || 'An unexpected error occurred',
      status: error.response?.status,
    };

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Try to refresh the token
      const refreshedToken = await refreshAuthToken();
      
      if (refreshedToken && error.config) {
        // Clone the original request with the new token
        const newConfig = { ...error.config };
        if (newConfig.headers) {
          newConfig.headers.Authorization = `Bearer ${refreshedToken}`;
        }
        // Retry the request with the new token
        return axios(newConfig);
      } else {
        // If refresh failed, dispatch auth error event
        window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { 
          detail: { message: 'Authentication failed' } 
        }));
      }
    }

    return Promise.reject(apiError);
  }
);

// API service with generic type support
const apiService = {
  /**
   * Perform a GET request
   */
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.get<T>(url, config);
    return response.data;
  },

  /**
   * Perform a POST request
   */
  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.post<T>(url, data, config);
    return response.data;
  },

  /**
   * Perform a PUT request
   */
  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.put<T>(url, data, config);
    return response.data;
  },

  /**
   * Perform a DELETE request
   */
  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.delete<T>(url, config);
    return response.data;
  },
};

export default apiService;